import * as legacyCustomer from "../../../../shared/types/customers";
import { APP_TYPE, AppType } from "../app.type";
import { AuthServiceType } from "./auth.service.type";
import {
  checkAuth,
  entraAdminConsent,
  entraCustomerDetails,
  entraLogin,
  login,
  logoutSession,
  resetPassword,
  saveNewPassword,
  verifyUsernameAndPassword
} from "../api.service";
import { Customer } from "../../../../shared/models/customer";
import { CustomerName } from "../../../../shared/config/customers";
import { EncodingUtils } from "../../../../shared/utils/encoding.utils";
import { Inject, Injectable } from "@angular/core";
import { Language } from "../../../../shared/settings/settings";
import { LoginDetails } from "../../../../shared/models/login-details";
import { LoginResult } from "../../../../shared/models/login-result";
import {
  NAVIGATION_HISTORY,
  REDIRECT_REASON,
  REDIRECT_TO
} from "../routing/routing.service";
import { PORTAL } from "../../../../shared/constants/portal";
import { ResetPasswordEmailDetails } from "../../../../shared/models/reset-password-email-details";
import { ResetPasswordObject } from "../../../../shared/models/reset-password-object";
import { ReturnUrlResult } from "../../../../shared/models/return-url-result";
import { TOKEN } from "./token";
import { UserIdleService } from "angular-user-idle";

function getDefaultCustomer(customerNames: CustomerName[]): CustomerName {
  if (customerNames.length > 0) {
    return customerNames[0];
  }

  return PORTAL;
}

@Injectable()
export class AuthService implements AuthServiceType {
  rolesArray: string[] = [];
  hasMTUPermission = false;
  isTeam = false;
  isAdmin = false;
  isUser = false;
  isRMAdmin = false;
  loginBtnDisabled = false;
  passwordLength: number = 8;
  loginAttempts: number = 0;
  entraBenefitsOpen: boolean = false;

  constructor(
    @Inject(APP_TYPE) private app: AppType,
    private userIdle: UserIdleService
  ) {}

  async loginToHomePage(details: LoginDetails) {
    this.app.run(async () => {
      await this.login(details);
    });
  }

  async login(details: LoginDetails) {
    const loginResult = await login(details);
    this.loginAttempts = this.getRemaining(loginResult);

    this.app.auth.passwordLength =
      loginResult.passLength != null
        ? loginResult.passLength
        : this.passwordLength;
    if (loginResult.authenticated) {
      if (loginResult.mfaRequired) {
        // Set username to localStorage to be able to access them on mfa-complete page
        localStorage.setItem("userId", loginResult.id);

        await this.app.routing.navigate("mfa-complete");
      } else {
        if (this.app.state.hasError) {
          this.app.state.hasError = false;
        }

        await this.handleAuthentication(loginResult);
      }
      this.loginBtnDisabled = false;
    } else {
      if (loginResult.passwordChangeRequired) {
        this.app.state.passwordChangeRequired = true;
        this.app.state.userId = loginResult.id;
        const defaultCustomer = getDefaultCustomer(loginResult.customerNames);
        this.app.state.next({
          customers: loginResult.customerNames,
          customer: defaultCustomer
        });
        return;
      }
      if (!loginResult.active && loginResult.loginAttempts === 3) {
        this.app.state.hasError = true;
        this.app.state.errorText = this.app.text.auth.userDeactivated;
        throw new Error("user deactivated");
      }

      if (loginResult.permissionDenied) {
        this.app.state.hasError = true;
        this.app.state.errorText = this.app.text.resetPassword.userNotFound;
      }
      if (
        loginResult.licenseAvailable != null &&
        !loginResult.licenseAvailable
      ) {
        this.app.state.hasError = true;
        this.app.state.errorText = this.app.text.auth.licenseNotAvailable;
        throw new Error("license not available");
      }
      if (!loginResult.active) {
        this.app.state.hasError = true;
        this.app.state.errorText = "user inactive";
        throw new Error("user inactive");
      }

      throw new Error("not logged in");
    }
  }

  async entraLogin(returnUrl: string): Promise<ReturnUrlResult> {
    return await entraLogin({
      returnUrl: returnUrl
    });
  }

  async entraAdminConsent(
    customerId: string,
    returnUrl: string
  ): Promise<ReturnUrlResult> {
    return await entraAdminConsent({
      customerId: customerId,
      returnUrl: returnUrl
    });
  }

  async entraCustomerDetails(customerCode: string): Promise<Customer> {
    return await entraCustomerDetails(customerCode);
  }

  getRemaining(loginResult: LoginResult): number {
    let remaining =
      loginResult.loginAttempts != null ? loginResult.loginAttempts : 0;
    if (remaining != 0) {
      return 3 - remaining;
    }
    if (remaining === 0) {
      return 0;
    }
    return 0;
  }

  async handleAuthentication(loginResult: LoginResult) {
    const {
      username,
      customerNames,
      token,
      passwordChangeRequired,
      roles,
      id,
      language,
      businessManager,
      businessArea,
      jobTitle
    } = loginResult;

    /** start watching expiration of the token */
    this.userIdle.startWatching();

    if (typeof Storage !== "undefined") {
      localStorage.setItem(TOKEN, EncodingUtils.encodeBase64(token));
    }

    const defaultCustomer = getDefaultCustomer(customerNames);
    if (defaultCustomer != null) {
      this.app.state.next({
        user: username,
        customers: customerNames,
        customer: defaultCustomer,
        isAnalyst: (roles as any)[defaultCustomer].analyst,
        isRMAdmin: (roles as any)[defaultCustomer].RMAdmin,
        passwordChangeRequired: loginResult.passwordChangeRequired,
        userId: id,
        language: language as Language,
        businessManager: businessManager ?? false,
        businessArea: businessArea ?? "",
        jobTitle: jobTitle ?? ""
      });
    }

    if (passwordChangeRequired) {
      return;
    }

    if (typeof Storage !== "undefined") {
      const segments = sessionStorage.getItem(REDIRECT_TO);
      if (segments !== null) {
        await this.app.routing.navigate(...JSON.parse(segments));
        return;
      }
    }

    // Set lastVersionSeen to localStorage to be able to access it on handleReleaseNotesModal
    const ownProfile = await this.app.profile.getProfile(id);
    const releaseVersions = ownProfile.releaseVersions;
    const lastVersion =
      releaseVersions != null
        ? releaseVersions[releaseVersions.length - 1]
        : "";
    localStorage.setItem("lastVersionSeen", lastVersion);

    await this.app.routing.navigateCustomer(defaultCustomer as CustomerName);
  }

  async checkAuth(): Promise<boolean> {
    const {
      authenticated,
      customerNames,
      token,
      username,
      passwordChangeRequired,
      roles,
      rolesArray,
      id,
      language,
      businessManager,
      businessArea,
      jobTitle
    } = await checkAuth();

    if (authenticated) {
      if (typeof Storage !== "undefined") {
        localStorage.setItem(TOKEN, EncodingUtils.encodeBase64(token));
      }

      const defaultCustomer = getDefaultCustomer(customerNames);
      if (defaultCustomer != null) {
        this.app.state.next({
          user: username,
          customers: customerNames,
          customer: defaultCustomer,
          isAnalyst: (roles as any)[defaultCustomer].analyst,
          isRMAdmin: (roles as any)[defaultCustomer].RMAdmin,
          passwordChangeRequired,
          language: language as Language,
          userId: id,
          businessManager: businessManager ?? false,
          businessArea: businessArea ?? "",
          jobTitle: jobTitle ?? ""
        });
        this.app.state.setLanguageChoice(language);
        this.rolesArray = rolesArray;
        if (
          this.rolesArray.findIndex((role: string) =>
            role.includes(legacyCustomer.Customer.MTU)
          ) !== -1
        ) {
          /** used for displaying a warning when trying to upload an attachment to posts */
          this.hasMTUPermission = true;
        }
      }
    } else {
      this.app.state.next({ user: null });
    }
    return authenticated;
  }

  async checkAuthNoDetails(): Promise<boolean> {
    const { authenticated } = await checkAuth();
    return authenticated;
  }

  async resetPassword(details: ResetPasswordEmailDetails) {
    await resetPassword(details);
  }

  async savePassword(details: ResetPasswordObject) {
    return await saveNewPassword(details);
  }

  async verifyOldPassword(user: string, password: string) {
    return await verifyUsernameAndPassword({ user, password });
  }

  async logout() {
    try {
      if (this.app.state.userId != null) {
        await logoutSession(
          this.app.customers.expectCurrent,
          this.app.state.userId
        );
      }
    } finally {
      if (typeof Storage !== "undefined") {
        if (localStorage.getItem(TOKEN) !== null) {
          localStorage.removeItem(TOKEN);
        }

        if (sessionStorage.getItem(REDIRECT_TO) !== null) {
          sessionStorage.removeItem(REDIRECT_TO);
        }

        if (sessionStorage.getItem(REDIRECT_REASON) !== null) {
          sessionStorage.removeItem(REDIRECT_REASON);
        }

        if (sessionStorage.getItem(NAVIGATION_HISTORY) !== null) {
          sessionStorage.removeItem(NAVIGATION_HISTORY);
        }

        this.app.state.filterByPage = [];
        this.app.filterList.field = null;
        this.app.filterFields.quickFilterSelected = "";
        this.app.filterTree.resetFilterTree();
      }

      this.app.state.next({ model: {}, numberOfUnreadAlerts: undefined });

      await this.app.routing.navigate("login");
    }
  }

  get landing() {
    return this.app.state.landing;
  }

  /** Get role of the logged in user */
  getCurrentUserRole() {
    if (
      this.app.auth.rolesArray.includes(
        `${this.app.customers.expectCurrent}-team`
      )
    ) {
      this.app.auth.isTeam = true;
    } else {
      this.app.auth.isTeam = false;
    }
    if (
      this.app.auth.rolesArray.includes(
        `${this.app.customers.expectCurrent}-admin`
      )
    ) {
      this.app.auth.isAdmin = true;
    } else {
      this.app.auth.isAdmin = false;
    }
    if (
      this.app.auth.rolesArray.includes(
        `${this.app.customers.expectCurrent}-user`
      )
    ) {
      this.app.auth.isUser = true;
    } else {
      this.app.auth.isUser = false;
    }
    if (
      this.app.auth.rolesArray.includes(
        `${this.app.customers.expectCurrent}-RMAdmin`
      )
    ) {
      this.app.auth.isRMAdmin = true;
    } else {
      this.app.auth.isRMAdmin = false;
    }
  }
}
